/**
 * Dialog for restoring a SQL Server database from a .bak file.
 */

import { useState, useEffect, useRef } from 'react';
import { Database } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { ProgressIndicator } from './ProgressIndicator';
import { ServerSideWarning, DestructiveWarning } from './WarningBanners';
import { RestoreFormFields } from './RestoreFormFields';
import { useRestoreBak } from '../hooks/useRestoreBak';
import { useOperationStatus } from '../hooks/useTaskProgress';
import { useBackupRestoreStore } from '../store/backupRestoreStore';
import { checkDatabaseExists, mssqlCancelBak, mssqlGetDefaultBackupDirectory } from '../services/backupRestoreApi';
import { toast } from '../../../components/ui/toaster';
import type { MssqlRecoveryState } from '../types/backupRestore';
import { getServerDirectoryFromFilePath, joinServerPath } from '../utils/serverPathUtils';
import { ServerPathPickerDialog } from './ServerPathPickerDialog';

type RestoreDatabaseDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
  /** Default database name (used as suggestion for target) */
  databaseName: string;
  /** Called when databases list should be refreshed after successful restore */
  onRefreshDatabases?: (connectionId: string) => void;
};

function buildDefaultRestoreServerFilePath(databaseName: string): string {
  const baseName = databaseName.trim() || 'database';
  return `C:\\Backups\\${baseName}.bak`;
}

export function RestoreDatabaseDialog({
  open,
  onOpenChange,
  connectionId,
  databaseName,
  onRefreshDatabases,
}: RestoreDatabaseDialogProps) {
  const [serverFilePath, setServerFilePath] = useState(
    buildDefaultRestoreServerFilePath(databaseName)
  );
  const [targetDatabaseName, setTargetDatabaseName] = useState(databaseName.trim());
  const [recoveryState, setRecoveryState] = useState<MssqlRecoveryState>('RECOVERY');
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [operationId, setOperationId] = useState<string | null>(null);
  const [databaseExists, setDatabaseExists] = useState(false);
  const [isCheckingDb, setIsCheckingDb] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [serverDefaultBackupDirectory, setServerDefaultBackupDirectory] = useState<string | null>(null);
  const [isDetectingBackupDirectory, setIsDetectingBackupDirectory] = useState(false);
  const [showServerFilePicker, setShowServerFilePicker] = useState(false);
  const hasTouchedServerFilePathRef = useRef(false);

  const operation = useOperationStatus(operationId);
  const { removeOperation, updateOperation } = useBackupRestoreStore();

  // Reset dialog state when opening or when invoked for a different database
  useEffect(() => {
    if (!open) {
      return;
    }

    hasTouchedServerFilePathRef.current = false;
    setServerFilePath(buildDefaultRestoreServerFilePath(databaseName));
    setTargetDatabaseName(databaseName.trim());
    setRecoveryState('RECOVERY');
    setOverwriteExisting(false);
    setOperationId(null);
    setDatabaseExists(false);
    setIsCheckingDb(false);
    setIsCancelling(false);
    setServerDefaultBackupDirectory(null);
    setIsDetectingBackupDirectory(false);
    setShowServerFilePicker(false);
  }, [open, databaseName]);

  // Cleanup when dialog closes
  useEffect(() => {
    if (open) {
      return;
    }

    setShowServerFilePicker(false);
    setServerDefaultBackupDirectory(null);
    setIsDetectingBackupDirectory(false);
    hasTouchedServerFilePathRef.current = false;
  }, [open]);

  // Detect SQL Server default backup directory (best-effort, server-side)
  useEffect(() => {
    if (!open || isDetectingBackupDirectory || serverDefaultBackupDirectory) {
      return;
    }

    setIsDetectingBackupDirectory(true);
    mssqlGetDefaultBackupDirectory(connectionId)
      .then((result) => {
        if (!result.success || !result.directory?.trim()) {
          return;
        }

        const directory = result.directory.trim();
        setServerDefaultBackupDirectory(directory);

        // Only auto-apply if the user hasn't changed the path yet
        if (!hasTouchedServerFilePathRef.current) {
          const baseName = databaseName.trim() || 'database';
          setServerFilePath(joinServerPath(directory, `${baseName}.bak`));
        }
      })
      .finally(() => {
        setIsDetectingBackupDirectory(false);
      });
  }, [open, isDetectingBackupDirectory, serverDefaultBackupDirectory, connectionId, databaseName]);

  const handleUseServerDefault = () => {
    if (!serverDefaultBackupDirectory?.trim()) {
      toast.error({
        title: 'Default Backup Folder Unavailable',
        description: 'Could not detect the SQL Server default backup directory. Please enter a server path manually.',
      });
      return;
    }

    hasTouchedServerFilePathRef.current = true;
    const baseName = databaseName.trim() || 'database';
    setServerFilePath(joinServerPath(serverDefaultBackupDirectory, `${baseName}.bak`));
  };

  // Check if target database exists when name changes
  useEffect(() => {
    if (!targetDatabaseName.trim() || !open) {
      setDatabaseExists(false);
      return;
    }

    const checkDb = async () => {
      setIsCheckingDb(true);
      try {
        const result = await checkDatabaseExists(connectionId, targetDatabaseName.trim());
        setDatabaseExists(result.exists);
      } catch {
        setDatabaseExists(false);
      } finally {
        setIsCheckingDb(false);
      }
    };

    const timer = setTimeout(checkDb, 300);
    return () => clearTimeout(timer);
  }, [targetDatabaseName, connectionId, open]);

  const { restoreDatabase, isRestoring, reset } = useRestoreBak({
    onOperationCreated: (opId) => setOperationId(opId),
    onSuccess: (result, opId) => {
      setOperationId(opId);

      if (result.success) {
        toast.success({
          title: 'Restore Complete',
          description: `Database "${targetDatabaseName}" restored successfully`,
        });
      } else if (result.cancelled) {
        toast.success({
          title: 'Restore Cancelled',
          description: `Restore of "${targetDatabaseName}" was cancelled`,
        });
      } else {
        toast.error({
          title: 'Restore Failed',
          description: result.error || 'Unknown error occurred',
        });
      }
    },
    onError: (error) => {
      toast.error({
        title: 'Restore Failed',
        description: error.message,
      });
    },
    onRefreshDatabases,
  });

  const handleStartRestore = () => {
    if (!serverFilePath.trim()) {
      toast.error({
        title: 'Invalid Path',
        description: 'Please enter a file path on the SQL Server',
      });
      return;
    }

    hasTouchedServerFilePathRef.current = true;

    if (!targetDatabaseName.trim()) {
      toast.error({
        title: 'Invalid Database Name',
        description: 'Please enter a target database name',
      });
      return;
    }

    if (databaseExists && !overwriteExisting) {
      toast.error({
        title: 'Database Exists',
        description: 'Target database already exists. Enable "Overwrite existing database" to replace it.',
      });
      return;
    }

    restoreDatabase({
      connectionId,
      serverFilePath: serverFilePath.trim(),
      targetDatabaseName: targetDatabaseName.trim(),
      recoveryState,
      overwriteExisting,
    });
  };

  const handleCancelRestore = async () => {
    if (!operationId || isCancelling) {
      return;
    }

    setIsCancelling(true);
    updateOperation(operationId, { message: 'Cancelling...' });

    try {
      const result = await mssqlCancelBak(operationId);
      if (!result.success) {
        toast.error({
          title: 'Cancel Failed',
          description: result.error || 'Unable to cancel the restore',
        });
        updateOperation(operationId, { message: result.error });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unable to cancel the restore';
      toast.error({
        title: 'Cancel Failed',
        description: errorMessage,
      });
      updateOperation(operationId, { message: errorMessage });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleClose = () => {
    if (operationId) {
      removeOperation(operationId);
    }
    setOperationId(null);
    reset();
    onOpenChange(false);
  };

  const isComplete =
    operation?.status === 'succeeded' ||
    operation?.status === 'failed' ||
    operation?.status === 'cancelled';
  const canClose = isComplete || (!isRestoring && !operationId);
  const hasStarted = isRestoring || operationId !== null;

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && canClose && handleClose()}>
        <DialogContent data-testid="restore-database-dialog">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
                <Database className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <DialogTitle>Restore Database (.bak)</DialogTitle>
                <DialogDescription className="mt-1">
                  Restore a SQL Server database from a backup file
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

        <div className="py-4 space-y-4">
          <ServerSideWarning />
          <DestructiveWarning testId="restore-destructive-warning" />

          {!hasStarted ? (
            <RestoreFormFields
              serverFilePath={serverFilePath}
              onServerFilePathChange={(value) => {
                hasTouchedServerFilePathRef.current = true;
                setServerFilePath(value);
              }}
              targetDatabaseName={targetDatabaseName}
              onTargetDatabaseNameChange={setTargetDatabaseName}
              recoveryState={recoveryState}
              onRecoveryStateChange={setRecoveryState}
              overwriteExisting={overwriteExisting}
              onOverwriteExistingChange={setOverwriteExisting}
              databaseExists={databaseExists}
              isCheckingDb={isCheckingDb}
              onUseServerDefault={handleUseServerDefault}
              isDetectingBackupDirectory={isDetectingBackupDirectory}
              serverDefaultBackupDirectory={serverDefaultBackupDirectory}
              onBrowseServerFile={() => setShowServerFilePicker(true)}
              databaseNameHint={databaseName.trim() || 'database'}
            />
          ) : (
            /* Progress indicator */
            <ProgressIndicator
              status={operation?.status || 'inProgress'}
              progress={operation?.progress}
              message={operation?.message}
              error={operation?.error}
              data-testid="restore-progress"
            />
          )}
        </div>

        <DialogFooter>
          {!hasStarted ? (
            <>
              <Button
                variant="outline"
                onClick={handleClose}
                data-testid="restore-dialog-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleStartRestore}
                disabled={!serverFilePath.trim() || !targetDatabaseName.trim() || (databaseExists && !overwriteExisting)}
                data-testid="restore-dialog-start"
                variant="destructive"
              >
                Start Restore
              </Button>
            </>
          ) : (
            <Button
              variant={isComplete ? 'default' : 'outline'}
              onClick={isComplete ? handleClose : handleCancelRestore}
              disabled={isComplete ? !canClose : isCancelling}
              data-testid="restore-dialog-close"
            >
              {isComplete ? 'Done' : isCancelling ? 'Cancelling...' : 'Cancel'}
            </Button>
          )}
        </DialogFooter>
        </DialogContent>
      </Dialog>

      <ServerPathPickerDialog
        open={showServerFilePicker}
        onOpenChange={setShowServerFilePicker}
        connectionId={connectionId}
        mode="file"
        initialDirectoryPath={
          serverDefaultBackupDirectory?.trim() ||
          getServerDirectoryFromFilePath(serverFilePath) ||
          'C:\\Backups'
        }
        title="Select Backup File (.bak)"
        description="Select a .bak file on the SQL Server machine to restore from."
        fileExtensionFilter=".bak"
        onSelectPath={(filePath) => {
          hasTouchedServerFilePathRef.current = true;
          setServerFilePath(filePath);
        }}
      />
    </>
  );
}
